/**
 * useProvisioning.ts — sandbox provisioning data hooks (DOPLA-730)
 *
 * Talks to the broker-API provision_sandbox endpoints (broker process, NOT
 * registry-mcp):
 *   GET  /api/broker/v1/provision-sandbox/drafts
 *   POST /api/broker/v1/provision-sandbox/consent
 *   POST /api/broker/v1/provision-sandbox
 *   POST /api/broker/v1/provision-sandbox/decommission   (R3 — teardown)
 *
 * Register-draft (R2) submits a service descriptor to the broker registry:
 *   POST /api/broker/v1/registry/services   body { descriptor } → 201 { service_id, state }
 *
 * Promote (R3) walks the ADR-059 registry lifecycle out of sandbox into a
 * vertical, admin-role-gated:
 *   POST /api/broker/v1/registry/services/{id}/validate      → 200 (draft→validated)
 *   POST /api/broker/v1/registry/services/{id}/publish       → 200 (validated→published)
 *   POST /api/broker/v1/registry/services/{id}/allocations   → 201 (allocate to vertical)
 *
 * All requests carry the Keycloak Bearer token (page is auth-gated).
 */

import { useCallback, useEffect, useReducer } from 'react';

export interface DraftRow {
  id: string;
  name: string | null;
  service_class: string | null;
  visibility: string | null;
  owner: string | null;
  custom_tenant_id: string | null;
  updated_at: string;
  /**
   * Added by the backend track; default 'draft' if absent. Widened for R3 to
   * cover the promote/decommission lifecycle states; unknown values render raw.
   */
  provision_status?:
    | 'draft' | 'provisioning' | 'provisioned'
    | 'decommissioning' | 'promoted' | 'allocated'
    | (string & {});
  /** Sandbox surface (from the latest provision event) — lets operators test the app pre-promotion. */
  dev_app_domain?: string | null;
}

/** The developer connection bundle — connections only, NEVER the membrane image. */
export interface ConnectionBundle {
  graphql_url: string;
  app_base: string;
  healthz: string;
  well_known: string;
  auth: {
    kind: string;
    keycloak_realm: string;
    client_id: string;
    token_url: string;
    direct_grant_example: string;
    note: string;
  };
  codegen: { instructions: string; schema: string };
  membrane: string;
}

export interface DeployInvocation {
  script: string;
  args: string[];
  env: Record<string, string>;
  commandline: string;
  note: string;
}

export interface ProvisionResult {
  draft_id: string;
  tenant: string;
  realm: { realm: string; client_id: string; minted: boolean };
  deploy_invocation: DeployInvocation;
  bundle: ConnectionBundle;
  live: boolean;
  note: string;
  consent_id?: string;
}

// ── Drafts list ──────────────────────────────────────────────────────────────

export type DraftsState =
  | { tag: 'idle' }
  | { tag: 'loading' }
  | { tag: 'loaded'; drafts: DraftRow[] }
  | { tag: 'error'; errorCode?: string };

type DraftsAction =
  | { type: 'START' }
  | { type: 'SUCCESS'; drafts: DraftRow[] }
  | { type: 'ERROR'; errorCode?: string };

function draftsReducer(_: DraftsState, a: DraftsAction): DraftsState {
  switch (a.type) {
    case 'START':   return { tag: 'loading' };
    case 'SUCCESS': return { tag: 'loaded', drafts: a.drafts };
    case 'ERROR':   return { tag: 'error', errorCode: a.errorCode };
    default:        return _;
  }
}

export function useDrafts(token: string | null) {
  const [state, dispatch] = useReducer(draftsReducer, { tag: 'idle' });

  const load = useCallback(() => {
    if (!token) return;
    dispatch({ type: 'START' });
    fetch('/api/broker/v1/provision-sandbox/drafts', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) { dispatch({ type: 'ERROR', errorCode: String(res.status) }); return; }
        return res.json() as Promise<{ drafts: DraftRow[] }>;
      })
      .then((data) => { if (data) dispatch({ type: 'SUCCESS', drafts: data.drafts }); })
      .catch(() => dispatch({ type: 'ERROR', errorCode: 'NETWORK_ERROR' }));
  }, [token]);

  useEffect(() => { load(); }, [load]);
  return { state, reload: load };
}

// ── Register draft (submit a descriptor to the broker registry) ───────────────

/**
 * Submit a service descriptor to the broker registry as a DRAFT.
 * POST /api/broker/v1/registry/services  body { descriptor } → 201 { service_id, state }.
 * Throws with the server's response body text on any non-2xx.
 */
export async function registerDraft(
  token: string,
  descriptor: Record<string, unknown>,
): Promise<{ service_id: string; state: string }> {
  const res = await fetch('/api/broker/v1/registry/services', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ descriptor }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `register failed (HTTP ${res.status})`);
  }
  return res.json() as Promise<{ service_id: string; state: string }>;
}

// ── Provision action (consent → provision) ───────────────────────────────────

/**
 * Record the ADR-068 §2 billable consent event, then invoke provision_sandbox.
 * Returns the connection bundle. Two calls, so the UI can present consent as an
 * explicit human-confirm step before provisioning. What was disclosed at consent
 * time is rendered by CostDisclosure and persisted with the record; no figure is
 * asserted anywhere (decision-2026-07-25-remove-fabricated-trial-cost).
 */
export async function recordConsent(
  token: string,
  draftId: string,
  tenant: string,
): Promise<void> {
  const res = await fetch('/api/broker/v1/provision-sandbox/consent', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ draft_id: draftId, tenant }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `consent failed (HTTP ${res.status})`);
  }
}

/**
 * Invoke provision_sandbox. Body is always { draft_id, tenant } — the operator
 * trigger is NEVER placed in the body; when supplied it rides as the
 * `X-Operator-Trigger` header (it authorizes but does not self-approve; the
 * backend decides). Consent is recorded separately by the caller beforehand.
 */
export async function provisionSandbox(
  token: string,
  draftId: string,
  tenant: string,
  operatorToken?: string,
): Promise<ProvisionResult> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  if (operatorToken) headers['X-Operator-Trigger'] = operatorToken;
  const res = await fetch('/api/broker/v1/provision-sandbox', {
    method: 'POST',
    headers,
    body: JSON.stringify({ draft_id: draftId, tenant }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string; message?: string };
    throw new Error(err.message ?? err.error ?? `provision failed (HTTP ${res.status})`);
  }
  return res.json() as Promise<ProvisionResult>;
}

// ── Promote action (ADR-068 §6 human MVP gate → ADR-059 registry lifecycle) ───

export interface AllocationResult {
  service_id?: string;
  vertical_broker_id?: string;
  state?: string;
  [k: string]: unknown;
}

/**
 * Promote a provisioned sandbox app out of sandbox into a vertical broker.
 * Walks the ADR-059 registry lifecycle in sequence — each step is admin-role
 * gated and depends on the previous one:
 *   1. validate  (draft→validated; requires the control-plane / provisioned app reachable)
 *   2. publish   (validated→published)   body { adr018_version_registered: true }
 *   3. allocate  (allocate to the vertical) body { vertical_broker_id }  → 201
 * Throws with the failing step name + the server's response text; returns the
 * allocation result on success.
 */
export async function promoteService(
  token: string,
  id: string,
  verticalBrokerId: string = 'deal-broker',
): Promise<AllocationResult> {
  const authJson = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  // 1. validate (draft → validated)
  const vRes = await fetch(`/api/broker/v1/registry/services/${id}/validate`, {
    method: 'POST',
    headers: authJson,
  });
  if (!vRes.ok) {
    const text = await vRes.text().catch(() => '');
    throw new Error(`validate failed (HTTP ${vRes.status})${text ? `: ${text}` : ''}`);
  }

  // 2. publish (validated → published)
  const pRes = await fetch(`/api/broker/v1/registry/services/${id}/publish`, {
    method: 'POST',
    headers: authJson,
    body: JSON.stringify({ adr018_version_registered: true }),
  });
  if (!pRes.ok) {
    const text = await pRes.text().catch(() => '');
    throw new Error(`publish failed (HTTP ${pRes.status})${text ? `: ${text}` : ''}`);
  }

  // 3. allocate to the vertical broker (→ 201)
  const aRes = await fetch(`/api/broker/v1/registry/services/${id}/allocations`, {
    method: 'POST',
    headers: authJson,
    body: JSON.stringify({ vertical_broker_id: verticalBrokerId }),
  });
  if (!aRes.ok) {
    const text = await aRes.text().catch(() => '');
    throw new Error(`allocate failed (HTTP ${aRes.status})${text ? `: ${text}` : ''}`);
  }
  return aRes.json().catch(() => ({})) as Promise<AllocationResult>;
}

// ── Promote-to-UAT action (WP-ACP-2 / Brief 12 — flag-gated, NO publish) ─────

/**
 * Promote a provisioned sandbox app to UAT via the flag-gated broker route.
 * Unlike promoteService this performs NO publish: the broker validates +
 * allocates + dispatches the ADR-075 register-app-uat pipeline; the app reaches
 * the store with status 'uat' and is published only after UAT approval +
 * set-public. Single POST; throws the server's response text on any non-2xx.
 */
export async function promoteServiceToUat(
  token: string,
  id: string,
  verticalBrokerId: string = 'deal-broker',
  tenant?: string,
): Promise<AllocationResult> {
  const res = await fetch(`/api/broker/v1/registry/services/${id}/promote-to-uat`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ vertical_broker_id: verticalBrokerId, ...(tenant ? { tenant } : {}) }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`promote-to-uat failed (HTTP ${res.status})${text ? `: ${text}` : ''}`);
  }
  return res.json().catch(() => ({})) as Promise<AllocationResult>;
}

// ── Decommission action (wf-teardown-instance; repo deletion is separate) ─────

export interface DecommissionResult {
  status: string;
  service_id: string;
}

/**
 * Request teardown of a provisioned sandbox (VM + ephemeral realm + DNS +
 * per-app secrets — wf-teardown-instance). Repo deletion is a SEPARATE
 * Category-A step and is NOT performed here. The operator trigger rides as the
 * `X-Operator-Trigger` header (gated like provision — 403 without it); the body
 * is always { draft_id, tenant }. Throws on any non-2xx; resolves to the 202
 * acknowledgement on success.
 */
export async function decommissionSandbox(
  token: string,
  id: string,
  tenant: string,
  operatorToken: string,
): Promise<DecommissionResult> {
  const res = await fetch('/api/broker/v1/provision-sandbox/decommission', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Operator-Trigger': operatorToken,
    },
    body: JSON.stringify({ draft_id: id, tenant }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `decommission failed (HTTP ${res.status})`);
  }
  return res.json() as Promise<DecommissionResult>;
}
